/**
 * The trailing draft row of an editable grid, as data.
 *
 * A grid that adds rows the way Session Variables and the table editor's column grid do has no
 * ADD button: its last row is always a blank one, typing into it makes it a row, and a fresh
 * blank row appears under it. What the grid shows is then never quite what gets saved — the
 * blank row is where the next row is written, not a row — so every save, hash and validator
 * reads the rows through `withoutBlankRows`, and whatever writes the rows keeps the invariant
 * through `ensureTrailingDraftRow` (or `planTrailingDraftRow`, when the rows live in a form list
 * that is edited by operations rather than replaced).
 */

/** What makes a row blank is the grid's own business: a key/value pair, a typed header, … */
export type DraftRowBlankTest<T> = (row: T | undefined) => boolean;

/** The list operations that bring rows back to exactly one blank row at the end. */
export interface IDraftRowPlan {
  /** Append one blank row: the last row is not blank (or there is no row at all). */
  append: boolean;
  /** Blank rows to drop from the end, keeping the first of the trailing run. Ascending. */
  removeIndexes: number[];
}

/**
 * Where the run of blank rows at the end of the list starts; `rows.length` when the last row is
 * not blank.
 */
const trailingBlankRunStart = <T>(rows: readonly T[], isBlank: DraftRowBlankTest<T>): number => {
  let start: number = rows.length;
  while (start > 0 && isBlank(rows[start - 1])) {
    start -= 1;
  }
  return start;
};

/**
 * What it takes to end the list on exactly one blank row. Of a trailing run of blank rows the
 * FIRST is kept: clearing the last real row leaves the caret in it, and that row — not the one
 * under it — becomes the draft, so nothing is remounted under the caret.
 */
export const planTrailingDraftRow = <T>(
  rows: readonly T[] | undefined,
  isBlank: DraftRowBlankTest<T>
): IDraftRowPlan => {
  const list: readonly T[] = rows ?? [];
  const start: number = trailingBlankRunStart(list, isBlank);
  if (start === list.length) {
    return { append: true, removeIndexes: [] };
  }

  const removeIndexes: number[] = [];
  for (let index = start + 1; index < list.length; index += 1) {
    removeIndexes.push(index);
  }
  return { append: false, removeIndexes };
};

/** Whether the list already ends on exactly one blank row. */
export const hasTrailingDraftRow = <T>(rows: readonly T[] | undefined, isBlank: DraftRowBlankTest<T>): boolean => {
  const plan: IDraftRowPlan = planTrailingDraftRow(rows, isBlank);
  return !plan.append && plan.removeIndexes.length === 0;
};

/** The rows, ending on exactly one blank row — for code that replaces a list wholesale (a load). */
export const ensureTrailingDraftRow = <T>(
  rows: readonly T[] | undefined,
  isBlank: DraftRowBlankTest<T>,
  makeBlank: () => T
): T[] => {
  const list: T[] = [...(rows ?? [])];
  const plan: IDraftRowPlan = planTrailingDraftRow(list, isBlank);
  if (plan.append) {
    return [...list, makeBlank()];
  }
  return list.slice(0, list.length - plan.removeIndexes.length);
};

/**
 * The rows that ARE rows: the trailing draft and any row cleared back to nothing are dropped.
 * What a save sends, what a hash compares and what a uniqueness check looks at.
 */
export const withoutBlankRows = <T>(rows: readonly T[] | undefined, isBlank: DraftRowBlankTest<T>): T[] =>
  (rows ?? []).filter((row: T): boolean => !isBlank(row));

/** A string cell counts as empty when it holds nothing but whitespace — or is missing altogether. */
export const isBlankText = (value: string | null | undefined): boolean => !value || value.trim() === "";
